import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, BookOpen, Calculator, Stethoscope, Globe, Target } from 'lucide-react';
import Footer from './Footer';
import AdvisorRibbon from './AdvisorRibbon';
import SEOHead from './SEOHead';

const CATEGORIES = {
  indian: {
    title: 'Indian Exams',
    subtitle: 'IIT-JEE, NEET-UG & competitive exam preparation',
    accentColor: 'text-orange-600',
    iconBg: 'bg-orange-500',
    Icon: Calculator,
    exams: [
      {
        id: 'free-trial',
        icon: Target,
        iconBg: 'bg-pink-500',
        textColor: 'text-pink-600',
        title: 'Free To Try all the Exams !',
        description: 'Exams: PLAB, USMLE, AMC, DHA, ADC, ORE, NAPLEX, PEBC',
        path: '/free-trial-exams',
        badge: 'FREE',
        image: 'https://customer-assets.emergentagent.com/job_learnsphere-105/artifacts/m4czax2x_5.png',
      },
      {
        id: 'global',
        icon: Globe,
        iconBg: 'bg-green-500',
        textColor: 'text-green-600',
        title: 'Go Global with GRE & GMAT',
        description: 'GRE and GMAT question banks for global aspirants',
        path: '/global-exam-bank',
        image: 'https://customer-assets.emergentagent.com/job_learnsphere-105/artifacts/8ccg7rqz_1.png',
      },
      {
        id: 'engineering',
        icon: Calculator,
        iconBg: 'bg-orange-500',
        textColor: 'text-orange-600',
        title: 'IIT-JEE & NEET-UG : Success Bank',
        description: 'Your Last Minute Success Prep for IIT-JEE & NEET-UG Exam',
        path: '/engineering-exam-bank',
        image: 'https://customer-assets.emergentagent.com/job_learnsphere-105/artifacts/0drmeg5z_3.png',
      },
    ],
  },
  international: {
    title: 'International Medical & Pharma Exams',
    subtitle: 'PLAB, USMLE, NAPLEX & global medical licensing exams',
    accentColor: 'text-emerald-600',
    iconBg: 'bg-emerald-500',
    Icon: Stethoscope,
    exams: [
      {
        id: 'medical',
        icon: Stethoscope,
        iconBg: 'bg-emerald-500',
        textColor: 'text-emerald-600',
        title: 'Doctors : Exam Success Bank',
        description: 'Medical: PLAB, USMLE, AMC, MCCQE, DHA & More',
        path: '/exam-bank',
        image: 'https://customer-assets.emergentagent.com/job_learnsphere-105/artifacts/o8hnj7cc_2.png',
      },
      {
        id: 'dental',
        icon: BookOpen,
        iconBg: 'bg-cyan-500',
        textColor: 'text-cyan-600',
        title: 'Dentist Global Success Bank',
        description: 'Dental: DHA Dental, ADC Part 1, ORE Part 1, INBDE',
        path: '/dentist-exam-bank',
        image: 'https://customer-assets.emergentagent.com/job_learnsphere-105/artifacts/updk27gm_8.png',
      },
      {
        id: 'pharmacy',
        icon: BookOpen,
        iconBg: 'bg-teal-500',
        textColor: 'text-teal-600',
        title: 'International Pharmacist Success Bank',
        description: 'Pharmacy: NAPLEX, MPJE, PEBC, GPhC, OPRA, CAP, DHA, SPLE',
        path: '/pharmacy-exam-bank',
        image: 'https://customer-assets.emergentagent.com/job_learnsphere-105/artifacts/04vx0zh6_4.png',
      },
    ],
  },
};

const ExamCard = ({ exam }) => {
  const navigate = useNavigate();
  const Icon = exam.icon;
  return (
    <div
      onClick={() => navigate(exam.path)}
      data-testid={`exam-card-${exam.id}`}
      className="cursor-pointer bg-white rounded-3xl overflow-hidden shadow-md border border-amber-300 hover:scale-[1.02] hover:shadow-xl transition-all duration-300 flex flex-col aspect-square"
    >
      {/* Image — 75% */}
      <div className="relative flex-[75] overflow-hidden">
        <img
          src={exam.image}
          alt={exam.title}
          className="w-full h-full object-cover"
          onError={e => {
            e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200"><rect fill="%23f3f4f6" width="200" height="200"/></svg>';
          }} loading="lazy" decoding="async" />
        <div className={`absolute top-3 left-3 w-10 h-10 sm:w-12 sm:h-12 ${exam.iconBg} rounded-xl flex items-center justify-center shadow-lg`}>
          <Icon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
        </div>
        {exam.badge && (
          <div className="absolute top-3 right-3 bg-pink-500 text-white text-xs sm:text-sm font-bold px-2.5 py-1 rounded-lg shadow-lg">
            {exam.badge}
          </div>
        )}
      </div>
      {/* Text — 25% */}
      <div className="flex-[25] p-3 sm:p-4 bg-white flex flex-col justify-center">
        <h3 className={`text-base sm:text-lg md:text-xl font-bold ${exam.textColor} mb-1`}>{exam.title}</h3>
        <p className="text-xs sm:text-sm md:text-base text-gray-600">{exam.description}</p>
      </div>
    </div>
  );
};

const ExamCategoryPage = () => {
  const navigate = useNavigate();
  const { category } = useParams();
  const data = CATEGORIES[category];

  if (!data) {
    navigate('/dashboard');
    return null;
  }

  const { title, subtitle, accentColor, iconBg, Icon, exams } = data;

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title={title}
        description={subtitle}
        path={`/exams/${category}`}
      />
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-gray-500 hover:text-gray-900 mb-1 text-xs sm:text-sm"
            data-testid="exam-category-back-btn"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Dashboard
          </button>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 ${iconBg} rounded-xl flex items-center justify-center shadow`}>
              <Icon className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className={`text-lg sm:text-xl font-bold ${accentColor}`}>{title}</h1>
              <p className="text-xs text-gray-500">{subtitle}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          {exams.map(exam => (
            <ExamCard key={exam.id} exam={exam} />
          ))}
        </div>
      </div>

      <AdvisorRibbon />
      <Footer />
    </div>
  );
};

export default ExamCategoryPage;
